import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { Trash2Icon } from "lucide-react";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { DeleteCommentSchema, deleteCommentSchema } from "@/features/tickets/schemas";
import { deleteTicketComment } from "@/features/tickets/services/ticket";
import { TicketComment } from "@/features/tickets/types/index";
import Error from "@/shared/components/Error";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface DeleteCommentFormProps {
  projectId: string;
  ticketId: string;
  comment: TicketComment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DeleteCommentForm({ projectId, ticketId, comment, open, onOpenChange }: DeleteCommentFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<DeleteCommentSchema>({
    resolver: zodResolver(deleteCommentSchema),
    defaultValues: {
      projectId: projectId,
      ticketId: ticketId,
      commentId: comment.id,
    },
  });

  const mutation = useMutation<undefined, AppError | Error, DeleteCommentSchema>({
    mutationFn: deleteTicketComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, projectId, ticketId], exact: true });
      onOpenChange(false);
    },
  });

  async function onSubmit(values: DeleteCommentSchema) {
    mutation.mutate(values);
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!mutation.isPending) {
          onOpenChange(newOpen);
        }
      }}
    >
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete comment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this comment{comment.replies.length > 0 ? " and all its replies" : ""}. This action <strong>cannot be undone</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to delete comment" error={mutation.error} />}
            <Controller
              name="projectId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <Controller
              name="ticketId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <Controller
              name="commentId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending || mutation.isSuccess}>Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" className="disabled:opacity-40" disabled={mutation.isPending || mutation.isSuccess}>
                <Trash2Icon />
                {mutation.isPending || mutation.isSuccess ? "Deleting comment..." : "Delete comment"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
